const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DNI_PATTERN = /^\d{8}$/;
const PHONE_PATTERN = /^\+?[0-9\s-]{7,24}$/;
const DIGITS_ONLY_PATTERN = /^\d+$/;
const TAB_CHAR_CODE = 9;
const LINE_FEED_CHAR_CODE = 10;
const CARRIAGE_RETURN_CHAR_CODE = 13;
const PRINTABLE_ASCII_MIN_CHAR_CODE = 32;
const PRINTABLE_ASCII_MAX_CHAR_CODE = 126;
const DELETE_CHAR_CODE = 127;
const CONTACT_FIRST_NAME_MAX_LENGTH = 80;
const CONTACT_LAST_NAME_MAX_LENGTH = 80;
const CONTACT_EMAIL_MAX_LENGTH = 254;
const CONTACT_PHONE_MAX_LENGTH = 24;
const CONTACT_PHONE_MIN_DIGITS = 7;
const CONTACT_MESSAGE_MAX_LENGTH = 1800;
const CONTACT_HONEYPOT_MAX_LENGTH = 255;
const COMPLAINT_CONSUMER_NAME_MAX_LENGTH = 120;
const COMPLAINT_DNI_LENGTH = 8;
const COMPLAINT_ADDRESS_MAX_LENGTH = 180;
const COMPLAINT_PHONE_MAX_LENGTH = 24;
const COMPLAINT_EMAIL_MAX_LENGTH = 254;
const COMPLAINT_DETAIL_MAX_LENGTH = 3000;
const PROVIDER_LEGAL_NAME_MAX_LENGTH = 160;
const PROVIDER_RUC_LENGTH = 11;
const PROVIDER_ADDRESS_MAX_LENGTH = 180;
const PROVIDER_EMAIL_MAX_LENGTH = 254;
const PDF_MARGIN_X = 56;
const PDF_BOTTOM_MARGIN = 64;
const PDF_LINE_HEIGHT = 15;
const PDF_INITIAL_Y = 790;
const PDF_PAGE_WIDTH = 595.28;
const PDF_PAGE_HEIGHT = 841.89;
const PDF_FONT_OBJECT_ID = 3;
const PDF_FIRST_PAGE_OBJECT_ID = 4;
const PDF_OBJECTS_PER_PAGE = 2;
const PDF_XREF_PAD_LENGTH = 10;
const PDF_BYTE_MASK = 0xff;
const PDF_UNDERLINE_LENGTH = 82;
const PDF_DEFAULT_WRAP_WIDTH = 78;
const PDF_DETAIL_WRAP_WIDTH = 88;
const PDF_NOTE_WRAP_WIDTH = 96;
const PDF_SIGNATURE_BLANK_HEIGHT = 24;
const PDF_RESPONSE_BLANK_HEIGHT = 12;
const PDF_SECTION_BLANK_HEIGHT = 5;
const PDF_TITLE_FONT_SIZE = 16;
const PDF_SECTION_FONT_SIZE = 11;
const PDF_BODY_FONT_SIZE = 10;
const PDF_NOTE_FONT_SIZE = 8;

export const PUBLIC_MARKETPLACE_LEGAL_IDENTITY = {
  legalName: 'CRYPTOLOGY S.A.C.',
  ruc: '20604970807',
  address: 'Calle Conde de la Monclova 378, 15073, San Isidro, Lima, Perú',
  contactEmail: 'hola@inmueble.red',
} as const;

export const PROSPECT_CLAIM_CONSENT_VERSION = 'prospect-claim-v1.2' as const;
export const COMMERCIAL_TERMS_VERSION = 'commercial-terms-v1.2' as const;
export const COMMERCIAL_TERMS_EFFECTIVE_LABEL =
  'Versión 1.2 | Vigente desde el 7 de octubre de 2026' as const;
export const COMMERCIAL_TERMS_PATH = '/terminos-de-uso-comercial' as const;

export const PUBLIC_MARKETPLACE_CONTACT_CHANNELS = {
  salesEmail: 'hola@inmueble.red',
  supportEmail: 'hola@inmueble.red',
  whatsappInternationalNumber: '51977169813',
  whatsappDisplayNumber: '+51 977 169 813',
  whatsappUrl:
    'https://wa.me/51977169813?text=Hola%2C%20quiero%20conocer%20m%C3%A1s%20sobre%20Inmueble.red.',
} as const;

export const PUBLIC_MARKETPLACE_CONTACT_REASON_VALUES = [
  'buyer_support',
  'publisher_support',
  'agency_sales',
  'listing_report',
  'privacy_request',
] as const;

export type PublicMarketplaceContactReason =
  (typeof PUBLIC_MARKETPLACE_CONTACT_REASON_VALUES)[number];

export const PUBLIC_MARKETPLACE_CONTACT_FIELD_LIMITS = {
  firstName: CONTACT_FIRST_NAME_MAX_LENGTH,
  lastName: CONTACT_LAST_NAME_MAX_LENGTH,
  email: CONTACT_EMAIL_MAX_LENGTH,
  phone: CONTACT_PHONE_MAX_LENGTH,
  message: CONTACT_MESSAGE_MAX_LENGTH,
  honeypot: CONTACT_HONEYPOT_MAX_LENGTH,
} as const;

export const PUBLIC_MARKETPLACE_COMPLAINT_BOOK_FIELD_LIMITS = {
  consumerName: COMPLAINT_CONSUMER_NAME_MAX_LENGTH,
  dni: COMPLAINT_DNI_LENGTH,
  address: COMPLAINT_ADDRESS_MAX_LENGTH,
  phone: COMPLAINT_PHONE_MAX_LENGTH,
  email: COMPLAINT_EMAIL_MAX_LENGTH,
  complaint: COMPLAINT_DETAIL_MAX_LENGTH,
} as const;

export interface PublicMarketplaceContactSubmission {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  reason: PublicMarketplaceContactReason;
  message: string;
  honeypot?: string;
}

export interface PublicMarketplaceContactResponse {
  success: true;
  requestId: string;
}

export interface PublicMarketplaceComplaintBookSubmission {
  consumerName: string;
  dni: string;
  address: string;
  phone: string;
  email: string;
  complaint: string;
}

export interface PublicMarketplaceComplaintBookProvider {
  legalName: string;
  ruc: string;
  address: string;
  email: string;
}

type PdfTextLine = {
  text: string;
  size?: number;
  bold?: boolean;
  indent?: number;
};

export class PublicMarketplaceLegalValidationError extends Error {}

const fail = (message: string): never => {
  throw new PublicMarketplaceLegalValidationError(message);
};

function hasDisallowedSingleLineCharacter(value: string): boolean {
  for (const character of value) {
    const code = character.charCodeAt(0);
    if (code < PRINTABLE_ASCII_MIN_CHAR_CODE || code === DELETE_CHAR_CODE) {
      return true;
    }
  }

  return false;
}

function isPdfSupportedCharacter(character: string): boolean {
  const code = character.charCodeAt(0);
  return (
    code === TAB_CHAR_CODE ||
    code === LINE_FEED_CHAR_CODE ||
    code === CARRIAGE_RETURN_CHAR_CODE ||
    (code >= PRINTABLE_ASCII_MIN_CHAR_CODE &&
      code <= PRINTABLE_ASCII_MAX_CHAR_CODE)
  );
}

function isAllowedMultilineCharacter(character: string): boolean {
  const code = character.charCodeAt(0);
  return (
    code === TAB_CHAR_CODE ||
    code === LINE_FEED_CHAR_CODE ||
    code === CARRIAGE_RETURN_CHAR_CODE ||
    (code >= PRINTABLE_ASCII_MIN_CHAR_CODE && code !== DELETE_CHAR_CODE)
  );
}

function stripDisallowedMultilineCharacters(value: string): string {
  let cleaned = '';
  for (const character of value) {
    if (isAllowedMultilineCharacter(character)) {
      cleaned += character;
    }
  }

  return cleaned;
}

function replaceUnsupportedPdfCharacters(value: string): string {
  let cleaned = '';
  for (const character of value) {
    cleaned += isPdfSupportedCharacter(character) ? character : '?';
  }

  return cleaned;
}

const requireNormalizedString = (value: unknown, fieldName: string): string => {
  const stringValue =
    typeof value === 'string'
      ? value
      : fail(`El campo ${fieldName} es obligatorio.`);
  const normalized = stringValue.normalize('NFC').trim();
  if (!normalized) {
    fail(`El campo ${fieldName} es obligatorio.`);
  }

  return normalized;
};

const ensureMaxLength = (
  value: string,
  fieldName: string,
  maxLength: number
): string => {
  if (value.length > maxLength) {
    fail(`El campo ${fieldName} excede el máximo de ${maxLength} caracteres.`);
  }

  return value;
};

const requireSingleLineText = (
  value: unknown,
  fieldName: string,
  maxLength: number
): string => {
  const normalized = requireNormalizedString(value, fieldName).replace(
    /\s+/g,
    ' '
  );

  if (hasDisallowedSingleLineCharacter(normalized)) {
    fail(`El campo ${fieldName} contiene caracteres no válidos.`);
  }

  return ensureMaxLength(normalized, fieldName, maxLength);
};

const requireOptionalSingleLineText = (
  value: unknown,
  fieldName: string,
  maxLength: number
): string | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }

  const stringValue =
    typeof value === 'string'
      ? value
      : fail(`El campo ${fieldName} no es válido.`);
  const normalized = stringValue.normalize('NFC').trim().replace(/\s+/g, ' ');
  if (!normalized) {
    return undefined;
  }

  if (hasDisallowedSingleLineCharacter(normalized)) {
    fail(`El campo ${fieldName} contiene caracteres no válidos.`);
  }

  return ensureMaxLength(normalized, fieldName, maxLength);
};

const requireMultilineText = (
  value: unknown,
  fieldName: string,
  maxLength: number
): string => {
  const normalized = requireNormalizedString(value, fieldName)
    .replace(/\r\n?/g, '\n')
    .trim();
  const cleaned = stripDisallowedMultilineCharacters(normalized).trim();
  if (!cleaned) {
    fail(`El campo ${fieldName} es obligatorio.`);
  }

  return ensureMaxLength(cleaned, fieldName, maxLength);
};

const validateDni = (dni: string): string => {
  if (!DNI_PATTERN.test(dni)) {
    fail('El DNI debe tener exactamente 8 dígitos.');
  }

  return dni;
};

const validateEmail = (email: string): string => {
  if (!EMAIL_PATTERN.test(email)) {
    fail('El correo electrónico no es válido.');
  }

  return email;
};

const validatePhone = (phone: string): string => {
  const digitCount = phone.replace(/\D/g, '').length;
  if (!PHONE_PATTERN.test(phone) || digitCount < CONTACT_PHONE_MIN_DIGITS) {
    fail('El número de teléfono no es válido.');
  }

  return phone;
};

const validateContactReason = (
  reason: string
): PublicMarketplaceContactReason => {
  if (
    !PUBLIC_MARKETPLACE_CONTACT_REASON_VALUES.includes(
      reason as PublicMarketplaceContactReason
    )
  ) {
    fail('El motivo de contacto no es válido.');
  }

  return reason as PublicMarketplaceContactReason;
};

export const parsePublicMarketplaceContactSubmission = (
  payload: unknown
): PublicMarketplaceContactSubmission => {
  if (!payload || typeof payload !== 'object') {
    fail('La solicitud no contiene un cuerpo JSON válido.');
  }

  const data = payload as Record<string, unknown>;
  const firstName = requireSingleLineText(
    data.firstName,
    'nombre',
    PUBLIC_MARKETPLACE_CONTACT_FIELD_LIMITS.firstName
  );
  const lastName = requireSingleLineText(
    data.lastName,
    'apellido',
    PUBLIC_MARKETPLACE_CONTACT_FIELD_LIMITS.lastName
  );
  const email = validateEmail(
    requireSingleLineText(
      data.email,
      'correo electrónico',
      PUBLIC_MARKETPLACE_CONTACT_FIELD_LIMITS.email
    )
  );
  const phone = validatePhone(
    requireSingleLineText(
      data.phone,
      'teléfono',
      PUBLIC_MARKETPLACE_CONTACT_FIELD_LIMITS.phone
    )
  );
  const reason = validateContactReason(
    requireSingleLineText(data.reason, 'motivo', PROVIDER_EMAIL_MAX_LENGTH)
  );
  const message = requireMultilineText(
    data.message,
    'mensaje',
    PUBLIC_MARKETPLACE_CONTACT_FIELD_LIMITS.message
  );
  const honeypot = requireOptionalSingleLineText(
    data.honeypot,
    'confirmación',
    PUBLIC_MARKETPLACE_CONTACT_FIELD_LIMITS.honeypot
  );

  if (honeypot) {
    fail('La solicitud de contacto no pudo ser aceptada.');
  }

  return {
    firstName,
    lastName,
    email,
    phone,
    reason,
    message,
    honeypot,
  };
};

export const parsePublicMarketplaceComplaintBookSubmission = (
  payload: unknown
): PublicMarketplaceComplaintBookSubmission => {
  if (!payload || typeof payload !== 'object') {
    fail('La solicitud no contiene un cuerpo JSON válido.');
  }

  const data = payload as Record<string, unknown>;
  const consumerName = requireSingleLineText(
    data.consumerName,
    'nombre completo',
    PUBLIC_MARKETPLACE_COMPLAINT_BOOK_FIELD_LIMITS.consumerName
  );
  const dni = validateDni(requireNormalizedString(data.dni, 'DNI'));
  const address = requireSingleLineText(
    data.address,
    'domicilio',
    PUBLIC_MARKETPLACE_COMPLAINT_BOOK_FIELD_LIMITS.address
  );
  const phone = validatePhone(
    requireSingleLineText(
      data.phone,
      'teléfono',
      PUBLIC_MARKETPLACE_COMPLAINT_BOOK_FIELD_LIMITS.phone
    )
  );
  const email = validateEmail(
    requireSingleLineText(
      data.email,
      'correo electrónico',
      PUBLIC_MARKETPLACE_COMPLAINT_BOOK_FIELD_LIMITS.email
    )
  );
  const complaint = requireMultilineText(
    data.complaint,
    'detalle del reclamo',
    PUBLIC_MARKETPLACE_COMPLAINT_BOOK_FIELD_LIMITS.complaint
  );

  return {
    consumerName,
    dni,
    address,
    phone,
    email,
    complaint,
  };
};

export const assertPublicMarketplaceComplaintBookProvider = (
  provider: Partial<PublicMarketplaceComplaintBookProvider>
): PublicMarketplaceComplaintBookProvider => {
  const legalName = requireSingleLineText(
    provider.legalName,
    'razón social',
    PROVIDER_LEGAL_NAME_MAX_LENGTH
  );
  const ruc = requireSingleLineText(provider.ruc, 'RUC', PROVIDER_RUC_LENGTH);
  const address = requireSingleLineText(
    provider.address,
    'domicilio del proveedor',
    PROVIDER_ADDRESS_MAX_LENGTH
  );
  const email = validateEmail(
    requireSingleLineText(
      provider.email,
      'correo del proveedor',
      PROVIDER_EMAIL_MAX_LENGTH
    )
  );

  if (ruc.length !== PROVIDER_RUC_LENGTH || !DIGITS_ONLY_PATTERN.test(ruc)) {
    fail('El RUC del proveedor debe tener exactamente 11 dígitos.');
  }

  return {
    legalName,
    ruc,
    address,
    email,
  };
};

const toPdfText = (value: string): string =>
  replaceUnsupportedPdfCharacters(
    value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/[–—]/g, '-')
    .replace(/°/g, 'o')
  );

const escapePdfLiteral = (value: string): string =>
  toPdfText(value).replace(/[\\()]/g, (character) => `\\${character}`);

const wrapText = (text: string, maxCharacters: number): string[] => {
  const lines: string[] = [];

  for (const paragraph of text.split('\n')) {
    const words = paragraph.trim().split(/\s+/).filter(Boolean);
    let currentLine = '';

    for (const word of words) {
      const nextLine = currentLine ? `${currentLine} ${word}` : word;

      if (nextLine.length > maxCharacters && currentLine) {
        lines.push(currentLine);
        currentLine = word;
        continue;
      }

      currentLine = nextLine;
    }

    if (currentLine) {
      lines.push(currentLine);
    }
  }

  return lines.length > 0 ? lines : [''];
};

const formatSubmittedAt = (submittedAt: Date): string => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    day: '2-digit',
    hour: '2-digit',
    hour12: false,
    minute: '2-digit',
    month: '2-digit',
    second: '2-digit',
    timeZone: 'America/Lima',
    year: 'numeric',
  }).formatToParts(submittedAt);
  const valueByType = Object.fromEntries(
    parts.map((part) => [part.type, part.value])
  );

  return `${valueByType.day}/${valueByType.month}/${valueByType.year} ${valueByType.hour}:${valueByType.minute}:${valueByType.second}`;
};

const buildPdfObjects = (pageStreams: string[]): string[] => {
  const objects: string[] = [
    '<< /Type /Catalog /Pages 2 0 R >>',
    `<< /Type /Pages /Kids [${pageStreams
      .map(
        (_, index) =>
          `${PDF_FIRST_PAGE_OBJECT_ID + index * PDF_OBJECTS_PER_PAGE} 0 R`
      )
      .join(' ')}] /Count ${pageStreams.length} >>`,
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>',
  ];

  pageStreams.forEach((stream, index) => {
    const pageObjectId =
      PDF_FIRST_PAGE_OBJECT_ID + index * PDF_OBJECTS_PER_PAGE;
    const streamObjectId = pageObjectId + 1;
    objects.push(
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PDF_PAGE_WIDTH} ${PDF_PAGE_HEIGHT}] /Resources << /Font << /F1 ${PDF_FONT_OBJECT_ID} 0 R >> >> /Contents ${streamObjectId} 0 R >>`,
      `<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`
    );
  });

  return objects;
};

const serializePdf = (objects: string[]): Uint8Array => {
  const parts = ['%PDF-1.4\n'];
  const offsets: number[] = [0];
  let byteLength = parts[0].length;

  objects.forEach((object, index) => {
    offsets.push(byteLength);
    const part = `${index + 1} 0 obj\n${object}\nendobj\n`;
    parts.push(part);
    byteLength += part.length;
  });

  const xrefOffset = byteLength;
  const xref = [
    `xref\n0 ${objects.length + 1}\n`,
    '0000000000 65535 f \n',
    ...offsets
      .slice(1)
      .map(
        (offset) =>
          `${String(offset).padStart(PDF_XREF_PAD_LENGTH, '0')} 00000 n \n`
      ),
    `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`,
  ].join('');
  const pdf = `${parts.join('')}${xref}`;
  const bytes = new Uint8Array(pdf.length);

  for (let index = 0; index < pdf.length; index += 1) {
    bytes[index] = pdf.charCodeAt(index) & PDF_BYTE_MASK;
  }

  return bytes;
};

export const createPublicMarketplaceComplaintBookPdf = (
  submission: PublicMarketplaceComplaintBookSubmission,
  provider: PublicMarketplaceComplaintBookProvider,
  submittedAt: Date
): Uint8Array => {
  const pageStreams: string[] = [];
  let currentPageLines: string[] = [];
  let y = PDF_INITIAL_Y;

  const newPage = (): void => {
    if (currentPageLines.length > 0) {
      pageStreams.push(currentPageLines.join('\n'));
    }

    currentPageLines = [];
    y = PDF_INITIAL_Y;
  };

  const addLine = ({
    text,
    size = PDF_BODY_FONT_SIZE,
    bold = false,
    indent = 0,
  }: PdfTextLine): void => {
    if (y < PDF_BOTTOM_MARGIN) {
      newPage();
    }

    const escaped = escapePdfLiteral(text);
    const fontWeightCommand = bold ? '2 Tr 0.35 w' : '0 Tr';
    currentPageLines.push(
      `BT /F1 ${size} Tf ${fontWeightCommand} 1 0 0 1 ${PDF_MARGIN_X + indent} ${y} Tm (${escaped}) Tj ET`
    );
    y -= PDF_LINE_HEIGHT;
  };

  const addBlank = (height = PDF_LINE_HEIGHT): void => {
    y -= height;
  };

  const addWrapped = (
    text: string,
    options?: Omit<PdfTextLine, 'text'> & { width?: number }
  ): void => {
    const width = options?.width ?? PDF_DEFAULT_WRAP_WIDTH;

    wrapText(text, width).forEach((line) => {
      addLine({
        text: line,
        size: options?.size,
        bold: options?.bold,
        indent: options?.indent,
      });
    });
  };

  const addSection = (title: string): void => {
    addBlank(PDF_SECTION_BLANK_HEIGHT);
    addLine({ text: title, size: PDF_SECTION_FONT_SIZE, bold: true });
    addLine({ text: ''.padEnd(PDF_UNDERLINE_LENGTH, '_') });
  };

  addLine({
    text: 'LIBRO DE RECLAMACIONES',
    size: PDF_TITLE_FONT_SIZE,
    bold: true,
  });
  addLine({
    text: 'Ley N° 29571 - Código de Protección y Defensa del Consumidor',
    size: PDF_SECTION_FONT_SIZE,
  });
  addBlank(PDF_NOTE_FONT_SIZE);

  addSection('DATOS DEL PROVEEDOR DEL PRODUCTO O SERVICIO');
  addLine({ text: `Razón social: ${provider.legalName}` });
  addLine({ text: `RUC: ${provider.ruc}` });
  addWrapped(`Domicilio: ${provider.address}`);
  addLine({ text: `Correo de atención: ${provider.email}` });

  addSection('DATOS DEL CONSUMIDOR');
  addLine({ text: `Nombre completo: ${submission.consumerName}` });
  addLine({ text: `DNI: ${submission.dni}` });
  addWrapped(`Domicilio: ${submission.address}`);
  addLine({ text: `Teléfono: ${submission.phone}` });
  addLine({ text: `Correo electrónico: ${submission.email}` });

  addSection('FECHA Y HORA DEL RECLAMO');
  addLine({ text: formatSubmittedAt(submittedAt) });

  addSection('DETALLE DEL RECLAMO');
  addWrapped(submission.complaint, { width: PDF_DETAIL_WRAP_WIDTH });

  addSection('FIRMA DEL CONSUMIDOR');
  addBlank(PDF_SIGNATURE_BLANK_HEIGHT);
  addLine({ text: '________________________________________' });

  addSection('RESPUESTA DEL PROVEEDOR');
  addLine({ text: '(Espacio reservado para la respuesta del proveedor)' });
  addBlank(PDF_RESPONSE_BLANK_HEIGHT);
  addLine({ text: ''.padEnd(PDF_UNDERLINE_LENGTH, '_') });
  addLine({ text: ''.padEnd(PDF_UNDERLINE_LENGTH, '_') });
  addLine({ text: ''.padEnd(PDF_UNDERLINE_LENGTH, '_') });

  addSection('FIRMA DEL PROVEEDOR');
  addBlank(PDF_SIGNATURE_BLANK_HEIGHT);
  addLine({ text: '________________________________________' });
  addBlank(PDF_RESPONSE_BLANK_HEIGHT);
  addWrapped(
    'Este documento registra la información declarada por el consumidor para su presentación ante el proveedor conforme a la normativa peruana aplicable.',
    { size: PDF_NOTE_FONT_SIZE, width: PDF_NOTE_WRAP_WIDTH }
  );

  newPage();

  return serializePdf(buildPdfObjects(pageStreams));
};
